/* ============================================================================
   Enshield public design system — APPROVED ARTWORK MANIFEST
   ----------------------------------------------------------------------------
   The approved astronaut compositions, with their intrinsic dimensions.

   Two reasons this is a manifest rather than inline string literals:

   1. Every image can then declare width/height, which reserves the box before
      the file arrives. Without it these transparent PNGs/WebPs are the single
      largest source of layout shift on the site.
   2. It makes astronaut placement visible and reviewable. The approved family
      supports meaningful scenes throughout the product, always outside
      critical data, forms, and actions. Semantic entries may reuse a pose.

   These are approved master assets: never regenerated, redrawn, recoloured
   or restyled. WebP is served because it is the optimised form of the same
   approved artwork; the PNG masters are retained in the repository.
   ========================================================================== */

export type Artwork = { src: string; width: number; height: number; alt: '' };

const art = (src: string, width: number, height: number): Artwork => ({ src, width, height, alt: '' });

export const ART = {
  /** Home hero — the one dominant composition on the site. */
  homeHero: art('/media/astronaut/astronaut-protect.png', 335, 490),
  /** Package/delivery pose for order journeys. */
  delivery: art('/media/astronaut/astronaut-protect.png', 335, 490),
  /** Seated-at-laptop pose. The operations/merchant supporting figure. */
  operations: art('/media/canonical-precision/platform-seated-laptop-approved-pose-v3.webp', 1200, 896),
  /** Technical laptop pose for integration narratives. */
  technical: art('/media/canonical-generated/product-proof-laptop-astronaut.webp', 1200, 896),
  /** Shield pose. Protection and intelligence narratives. */
  shield: art('/media/canonical-precision/intelligence-shield-approved-pose-v1.webp', 896, 1200),
  /** Alternate shield pose reserved for pricing/protection decisions. */
  pricingShield: art('/media/canonical-generated/merchant-pricing-shield-v1.webp', 896, 1200),
  /** Magnifier pose. Detection, review and lookup narratives. */
  magnifier: art('/media/canonical-precision/workflow-magnifier-approved-pose-v1.webp', 1200, 896),
  /** Protect pose. Claim and support narratives. */
  protect: art('/media/canonical-precision/workflow-protect-approved-pose-v1.webp', 1200, 896),
  /** Storefront pose. The merchant hero. */
  storefront: art('/media/canonical-generated/merchant-storefront-hero-v1.webp', 1200, 896),
  /** Reading pose. The FAQ/help hero. */
  reading: art('/media/canonical-generated/faq-reading-astronaut-v1.webp', 896, 1200),
  /** Magnifier-at-scale pose. The claim tracking hero. */
  tracking: art('/media/astronaut/astronaut-inspect.png', 520, 452),
  /** Evidence pose. Claim filing. */
  evidence: art('/media/canonical-generated/claim-evidence-astronaut.webp', 1200, 896),
  /** Analytics pose. The claim-entry hero. */
  analytics: art('/media/canonical-generated/login-analytics-astronaut.webp', 1200, 896),
  /** Data/reporting pose for proof and testimonial moments. */
  testimonialAnalytics: art('/media/canonical-generated/merchant-testimonial-analytics-v1.webp', 1200, 896),
  /** Compatibility key: the rejected pair is replaced by the approved shield guide. */
  ctaPair: art('/media/canonical-generated/merchant-pricing-shield-v1.webp', 896, 1200),
} as const;

export type ArtKey = keyof typeof ART;
